import { computed, effect, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { FilterSpecification } from 'maplibre-gl';
import { firstValueFrom } from 'rxjs';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import {
    BroadcastChannelMessageEnum,
    BroadcastChannelNameEnum,
    BroadcastChannelSourceEnum,
} from '@connect-services/client/models/broadcast-channel-data.model';
import { BroadcastChannelService } from '@connect-services/client/services/broadcast-channel.service';
import { FilterService } from '@connect-services/client/services/filter.service';
import { ProjectSessionStorageService } from '@connect-services/client/services/project-session-storage.service';
import {
    LayerFilterResult,
    MapFilter,
    MapFilterPreviewDto,
    MapFilterService as SdkMapFilterService,
} from '@connect-services/src/sdk/api';
import {
    defaultPaginationParameters,
    OrderEnum,
    paginationMaxTake,
    PaginationParameters,
} from '@shared/client/models/pagination.model';

export type MapFilterDto = {
    filter?: MapFilter;
};

@Injectable({
    providedIn: 'root',
})
export class MapFilterService extends FilterService<MapFilterDto> {
    public readonly appliedFiltersResult = signal<LayerFilterResult[]>([]);
    public override readonly filterElementsCount: Signal<number> = computed<number>(
        () => this.selectedFilters().length,
    );
    public readonly filters: WritableSignal<MapFilter[]> = signal<MapFilter[]>([]);
    public readonly hasResolvedAppliedFilters = signal(false);
    public initialFilterByLayer = new Map<string, FilterSpecification | undefined>();
    public readonly previewResultFilters = signal<LayerFilterResult[] | null>(null);
    public readonly selectedFilters: WritableSignal<string[]> = signal<string[]>([]);

    private appliedFilters: string[] = [];
    private broadcastChannel = new BroadcastChannel(BroadcastChannelNameEnum.LAYERS);
    private readonly broadcastChannelService = inject(BroadcastChannelService);
    private readonly projectBloc: ProjectBloc = inject(ProjectBloc);
    private readonly projectSessionStorageService: ProjectSessionStorageService = inject(ProjectSessionStorageService);
    private readonly sdkMapFilterService: SdkMapFilterService = inject(SdkMapFilterService);
    private readonly translocoService = inject(TranslocoService);

    public constructor() {
        super();

        this.broadcastChannel.onmessage = (event: MessageEvent): void => {
            if (
                event.data.source !== BroadcastChannelSourceEnum.TABLE_VIEW ||
                event.data.message !== BroadcastChannelMessageEnum.APPLY_FILTERS_SUCCESS ||
                this.selectedFilters().sort().join('') === event.data.additionalData.sort().join('')
            ) {
                return;
            }

            this.selectedFilters.set(event.data.additionalData);
        };

        effect(async () => {
            if (!this.projectBloc.value()) {
                return;
            }

            const sessionStorage = this.projectSessionStorageService.getProjectStorage(this.projectBloc.value()!.id);
            if (sessionStorage?.selectedFilters?.length) {
                this.selectedFilters.set(sessionStorage.selectedFilters);
            } else {
                this.selectedFilters.set([]);
            }

            await this.initData();
        });

        effect(() => {
            this.selectedFilters();
            this.hasResolvedAppliedFilters.set(false);
        });

        effect(() => {
            this.broadcastChannelService.updateTabsEntities(this.broadcastChannel, this.selectedFilters());
        });
    }

    public buildTooltipMessageOnDelete(filterName: string | undefined, wasActive: boolean): string {
        if (!filterName) {
            return this.translocoService.translate('mapFilter.filterDeletedInYourProject');
        }

        if (!wasActive) {
            return this.translocoService.translate('mapFilter.filterDeletedInYourProjectWithName', {
                filterName: filterName,
            });
        }

        return this.translocoService.translate('mapFilter.activeFilterDeletedInYourProjectWithName', {
            filterName: filterName,
        });
    }

    public clearFilters(): void {
        this.appliedFilters = [];
        this.appliedFiltersResult.set([]);
        this.hasResolvedAppliedFilters.set(false);
    }

    public clearPreview(): void {
        this.previewResultFilters.set(null);
    }

    public deleteLocalFilter(filterId: string): { filterName: string | undefined; wasActive: boolean } {
        const filter: MapFilter | undefined = this.filters().find((filter: MapFilter) => filter.id === filterId);

        if (!filter) {
            return { filterName: undefined, wasActive: false };
        }

        const isUsed: boolean = this.selectedFilters().includes(filter.id);

        this.updateData(filter.id);

        return { filterName: filter.name, wasActive: isUsed };
    }

    public override async initData(): Promise<void> {
        if (!this.projectBloc.value()?.id) {
            return;
        }

        const pagination: PaginationParameters = {
            ...defaultPaginationParameters,
            isCaseSensitive: true,
            order: OrderEnum.ASC,
            orderBy: 'name',
            take: paginationMaxTake,
        };

        let page = 0;
        let response;
        const filters: MapFilter[] = [];
        do {
            response = await firstValueFrom(
                this.sdkMapFilterService.mapFilterControllerGet({
                    ...pagination,
                    page,
                    project: this.projectBloc.value()!.id,
                }),
            );

            page += 1;
            filters.push(...response.items);
        } while (response.hasNextPage);

        this.filters.set(filters);
    }

    public async preview(mapFilterPreview: MapFilterPreviewDto): Promise<void> {
        const filters: LayerFilterResult[] = await firstValueFrom(
            this.sdkMapFilterService.mapFilterControllerPreview({ mapFilterPreviewDto: mapFilterPreview }),
        );

        this.previewResultFilters.set(filters);
    }

    public async updateAppliedFilters(): Promise<void> {
        if (this.previewResultFilters() !== null) {
            this.appliedFiltersResult.set(this.previewResultFilters()!);
            this.hasResolvedAppliedFilters.set(true);

            return;
        }

        const hasSelectionChanged =
            this.selectedFilters().length !== this.appliedFilters.length ||
            this.selectedFilters().some((id) => !this.appliedFilters.includes(id)) ||
            this.appliedFilters.some((id) => !this.selectedFilters().includes(id));

        this.appliedFilters = this.selectedFilters();

        if (!hasSelectionChanged) {
            this.hasResolvedAppliedFilters.set(true);

            return;
        }

        this.appliedFiltersResult.set(
            await firstValueFrom(
                this.sdkMapFilterService.mapFilterControllerApplyFilters({
                    filters: this.selectedFilters(),
                    project: this.projectBloc.value()!.id,
                }),
            ),
        );
        this.hasResolvedAppliedFilters.set(true);
    }

    private updateData(filterId: string): void {
        this.filters.set(this.filters().filter((filter: MapFilter) => filter.id !== filterId));
        this.selectedFilters.set(this.selectedFilters().filter((id: string) => id !== filterId));

        this.projectSessionStorageService.updateSelectedFilter(this.projectBloc.value()!.id, this.selectedFilters());
    }
}
